import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { intersectionWith } from "lodash";
import { TablePro, type TableProColumnType } from "@gw/web-basic-components";
import { type IColumn } from "@gw/web-business-components";
import { pick } from "@gw/gw-utils";
import { AdvancedSearch, InnerSearch, InnerSelect } from "./components";
import { useSearchGroup } from "../search-group/hooks";
import { SearchGroup } from "..";
import { removeNullProperties } from "../search-group/utils";
import type { InnerSearchItem, IProTableProps } from "./dto";
import type { IAdvancedSearchProps } from "./components/advanced-search/dto";

interface InnerTableProps extends IProTableProps<any> {
  /** columns版本，变更后会重新渲染columns部分 */
  columnsVersion: number;
  /** 初始全量查询条件 */
  defaultSearch: IReqPage;
  /** 强制刷新回调 */
  handleForceUpdate: (scope?: "table" | "columns") => void;
}
/** 内部定式表格 */
const InnerTable: React.FC<InnerTableProps> = (props) => {
  const {
    searchConfig,
    advancedSearch,
    columns,
    pagination,
    columnsVersion,
    onChange,
    renderTableAbove,
    handleForceUpdate,
    defaultSearch,
    ...restProps
  } = props;

  /** 控制高级搜索展开收起 */
  const [expand, setExpand] = useState(
    !!(advancedSearch as IAdvancedSearchProps)?.expanded
  );

  /** 获取SeearhGroup内部Api */
  const { query, dispatch, submit, reset } = useSearchGroup<IReqPage>();

  const onSubmit = () => {
    submit();
  };

  const onReset = () => {
    reset();
    handleForceUpdate("columns");
    submit();
  };

  const onAdvanceExpand = () => {
    setExpand((pre) => !pre);
  };

  /** 表格上方的渲染结果 */
  const innerRenderAbove = useMemo(() => {
    const doms: InnerSearchItem = {} as InnerSearchItem;
    if (searchConfig)
      doms.InnerSearch = (
        <InnerSearch
          key="InnerSearch"
          {...pick(searchConfig, [
            "allowClear",
            "tooltipTitle",
            "tooltipContent",
            "placeholder",
            "column",
          ])}
          onSubmit={onSubmit}
        />
      );

    if (advancedSearch) {
      doms.AdvancedSearch = (
        <AdvancedSearch
          key="AdvancedSearch"
          onExpand={onAdvanceExpand}
          onReset={onReset}
          onSearch={onSubmit}
          {...(typeof advancedSearch === "object" ? advancedSearch : {})}
        />
      );
    }
    if (!renderTableAbove)
      return (
        <div
          className={classNames("wbuc-pro-table-search", {
            "wbuc-pro-table-search-left": searchConfig?.position === "left",
          })}
        >
          {Object.getOwnPropertyNames(doms).map((domKey) => doms[domKey])}
        </div>
      );
    const newDoms = renderTableAbove(doms, { onSubmit, onReset }) || [];

    return newDoms;
  }, [searchConfig, renderTableAbove]);

  /** 内部columns*/
  const customColumns = useMemo(() => {
    if (!query) return columns;

    const { queryConditions } = query;

    return columns.map((col) => {
      const { renderSearch, filterDropdown, filters } = col;
      if (renderSearch || filters || filterDropdown) {
        // 获取列在搜索条件中的索引
        const colIdxInSearch = queryConditions.findIndex((q) => {
          if (!q?.column) return false;
          return [col.searchColumn, col.key, col.dataIndex].includes(q?.column);
        });

        // 按索引获取条件对象
        const conditionInIdx = queryConditions[colIdxInSearch];

        // antd-table 只有在为数组的情况下才会高亮搜索 icon
        const value = Array.isArray(conditionInIdx?.matchValue)
          ? conditionInIdx?.matchValue
          : [conditionInIdx?.matchValue];
        // 渲染自定义筛选组件
        let SearchComponent: React.ReactNode;

        if (renderSearch) {
          SearchComponent = renderSearch;
        } else if (filters) {
          SearchComponent = (
            <InnerSelect
              options={filters.map((item) => ({
                label: item.text,
                value: item.value as string,
              }))}
            />
          );
        }

        return {
          ...col,
          filteredValue: conditionInIdx?.matchValue ? value : null,
          keyPath: ["queryConditions", colIdxInSearch, "matchValue"],
          ...(filterDropdown && {
            filterDropdown: filterDropdown
              ? (filterProps) => {
                  return (
                    <SearchGroup.Item
                      keyPath={[
                        "queryConditions",
                        colIdxInSearch,
                        "matchValue",
                      ]}
                      {...pick(col, ["valueKey", "trigger", "targetKey"])}
                    >
                      {filterDropdown?.(filterProps) || SearchComponent}
                    </SearchGroup.Item>
                  );
                }
              : void 0,
          }),
          renderSearch: SearchComponent ? (
            <SearchGroup.Item
              keyPath={["queryConditions", colIdxInSearch, "matchValue"]}
              {...pick(col, ["valueKey", "trigger", "targetKey"])}
            >
              {SearchComponent}
            </SearchGroup.Item>
          ) : (
            void 0
          ),
        };
      }

      return {
        ...col,
      };
    });
  }, [
    (columns?.map((m) => `${m.key}${m.dataIndex}`) || []).join(""),
    columnsVersion,
  ]);

  /** 初始化时执行查询 */
  useEffect(() => {
    submit();
  }, []);

  return (
    <div className="wbuc-pro-table-container">
      {innerRenderAbove}
      <div className="wbuc-pro-table">
        <TablePro
          openProFilter={expand}
          columns={customColumns as TableProColumnType<any>[]}
          pagination={
            pagination
              ? {
                  ...pagination,
                  pageSize: query.size,
                  current: query.current,
                  onChange: (current, size) => {
                    const newV = new Map([
                      ["current", current],
                      ["size", size],
                    ]);
                    dispatch(newV);
                    pagination?.onChange?.(current, size);
                  },
                }
              : false
          }
          onChange={(page, filters, sorter, extra) => {
            const { action } = extra;

            switch (action) {
              case "paginate":
                submit();
                break;
              case "sort":
                if (Array.isArray(sorter)) {
                  console.error("只支持单列排序");
                } else {
                  const { columnKey, order } = sorter;
                  const index = defaultSearch.orders?.findIndex?.(
                    (c) => c?.column === columnKey
                  );
                  if (index >= 0) {
                    const value = order ? order === "ascend" : null;
                    dispatch(new Map([[["orders", index, "asc"], value]]));
                  }
                  submit();
                }
                break;
              case "filter":
                const { queryConditions } = query;

                const arr = [];
                // 自定义表头筛选项的列 -->fixed 使用内置搜索的列
                const actrualColsWithFilter = columns.filter(
                  (col) => col.filters
                );
                // onChange 携带的 列-筛选值
                const filterObjectKey = Object.keys(filters);

                // column中定义的key或dataIndex匹配filters
                const intersectionKey: string[] = intersectionWith(
                  filterObjectKey,
                  actrualColsWithFilter,
                  (a: string, b: IColumn<any>) =>
                    [b.key, b.dataIndex].filter((m) => m).includes(a)
                );

                queryConditions.forEach((q, index) => {
                  if (!q) {
                    return;
                  }
                  if (
                    intersectionKey.findIndex((col) => col === q.column) > -1
                  ) {
                    // 目前 Table 表头仅支持单选，返回值需要被转译成字符串
                    const value = Array.isArray(filters?.[q.column])
                      ? filters?.[q.column].join("")
                      : filters?.[q.column];

                    arr.push([["queryConditions", index, "matchValue"], value]);
                  }
                });
                dispatch(new Map(arr));
                submit();
                break;
              default:
                submit();
            }
            const noNullQuery = removeNullProperties(query);
            onChange?.(noNullQuery, query);
          }}
          {...restProps}
        />
      </div>
    </div>
  );
};

export default InnerTable;
